import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { PRICE, WEBINAR_LINE } from "@/config";
import { Countdown } from "@/components/Countdown";

const lineVariants = {
    hidden: { y: "115%" },
    visible: (i) => ({
        y: "0%",
        transition: { duration: 1.1, delay: 0.9 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
    }),
};

const fadeUp = (delay) => ({
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const coverY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 120, damping: 18 });

    const onMouseMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section ref={ref} onMouseMove={onMouseMove} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-40 -left-40 w-[85vw] h-[85vw] rounded-full bg-royal/20 blur-[110px]" />
                <div className="absolute top-1/4 -right-48 w-[75vw] h-[75vw] rounded-full bg-orchid/20 blur-[120px]" />
                <div className="absolute -bottom-48 left-1/4 w-[60vw] h-[60vw] rounded-full bg-magenta/10 blur-[120px]" />
                <div className="absolute bottom-0 right-1/4 w-[40vw] h-[40vw] rounded-full bg-gold/10 blur-[110px]" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 pt-28 sm:pt-32 pb-20 w-full grid lg:grid-cols-[1.15fr_0.85fr] items-center gap-14 lg:gap-8">
                <motion.div style={{ y: textY, opacity: fade }} className="min-w-0">
                    <motion.p {...fadeUp(1.5)} className="font-mono text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.35em] uppercase text-gold mb-7" data-testid="hero-overline">
                        Toriano Sanzone — AI + Marketing
                    </motion.p>

                    <h1 className="font-serif uppercase font-semibold tracking-tight leading-[1.04] text-[10.5vw] sm:text-7xl lg:text-8xl [text-shadow:0_2px_50px_rgba(11,6,20,0.6)]" data-testid="hero-headline">
                        <span className="block overflow-hidden pb-1">
                            <motion.span custom={0} variants={lineVariants} initial="hidden" animate="visible" className="block">Consciousness</motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span custom={1} variants={lineVariants} initial="hidden" animate="visible" className="block gold-text italic font-medium">of Abundance</motion.span>
                        </span>
                    </h1>

                    <motion.p {...fadeUp(1.7)} className="mt-7 font-sans font-light tracking-[0.18em] sm:tracking-[0.25em] uppercase text-[11px] sm:text-lg text-zinc-200" data-testid="hero-subheadline">
                        <span className="violet-text font-medium">AI.</span> Marketing. Mindset. Opportunity.
                    </motion.p>

                    <motion.p {...fadeUp(1.85)} className="mt-5 max-w-xl text-sm sm:text-base text-zinc-400 leading-relaxed" data-testid="hero-supporting-text">
                        Learn how Toriano Sanzone combines artificial intelligence, marketing, branding, creativity and abundance thinking to create new opportunities.
                    </motion.p>

                    <motion.div {...fadeUp(2.0)} className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5">
                        <button
                            data-testid="hero-cta-button"
                            onClick={() => scrollToId("checkout")}
                            className="group relative w-full sm:w-auto overflow-hidden bg-gold text-black font-mono text-xs sm:text-sm tracking-[0.2em] uppercase px-10 py-5 hover:bg-gold-light transition-colors duration-300 shadow-[0_10px_60px_rgba(168,85,247,0.35)]"
                        >
                            <span className="relative z-10">Get Instant Access — {PRICE}</span>
                            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                        </button>
                        <div data-testid="hero-price-badge" className="font-mono text-[11px] tracking-[0.2em] sm:tracking-[0.25em] uppercase text-zinc-400 text-center sm:text-left leading-relaxed">
                            <span className="text-gold">{PRICE}</span> — Digital Book + Weekly Live Webinar
                        </div>
                    </motion.div>

                    <motion.p {...fadeUp(2.15)} className="mt-6 font-mono text-[10px] sm:text-[11px] tracking-[0.18em] sm:tracking-[0.25em] uppercase text-orchid/90 leading-relaxed" data-testid="hero-webinar-note">
                        Plus: {WEBINAR_LINE}
                    </motion.p>

                    <motion.div {...fadeUp(2.3)}>
                        <Countdown />
                    </motion.div>
                </motion.div>

                <motion.div style={{ y: coverY }} className="flex justify-center lg:justify-end">
                    <motion.div
                        initial={{ opacity: 0, y: 60 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.2, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
                        style={{ perspective: 1000 }}
                    >
                        <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
                            <motion.div
                                animate={{ y: [0, -14, 0] }}
                                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                                className="relative"
                            >
                                <div className="absolute -inset-8 bg-orchid/35 blur-3xl rounded-full" />
                                <img
                                    src="/book-cover.png"
                                    alt="Consciousness of Abundance — book cover"
                                    data-testid="hero-book-cover"
                                    className="relative w-44 sm:w-60 lg:w-80 border border-gold/40 shadow-[0_0_90px_rgba(139,92,246,0.4)]"
                                />
                                <div className="absolute inset-0 bg-gradient-to-tr from-royal/25 via-transparent to-gold/10 pointer-events-none" />
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>

            <motion.button
                data-testid="hero-scroll-indicator"
                onClick={() => scrollToId("what-you-get")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.6, duration: 1 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gold/60 hover:text-gold transition-colors"
                aria-label="Scroll down"
            >
                <ArrowDown className="w-5 h-5 animate-bounce" />
            </motion.button>
        </section>
    );
};
